export const DEVICE_ID_STORAGE_KEY = 'trustedDeviceId';

// RFC 4122 v4 UUID fallback for browsers without crypto.randomUUID
const generateUuidV4 = () => {
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));

  /* eslint-disable no-bitwise */
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  /* eslint-enable no-bitwise */

  const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');

  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

/**
 * Returns the persistent device id (GUID), creating it on first use
 *
 * @returns {string}
 */
export const getDeviceId = () => {
  let deviceId = localStorage.getItem(DEVICE_ID_STORAGE_KEY);

  if (!deviceId) {
    deviceId = window.crypto.randomUUID ? window.crypto.randomUUID() : generateUuidV4();
    localStorage.setItem(DEVICE_ID_STORAGE_KEY, deviceId);
  }

  return deviceId;
};

const detectOs = ua => {
  if (/Windows/i.test(ua)) return 'Windows';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'iOS';
  if (/Android/i.test(ua)) return 'Android';
  if (/Mac OS/i.test(ua)) return 'macOS';
  if (/Linux/i.test(ua)) return 'Linux';

  return undefined;
};

// non-Chromium entries like "Not A;Brand" carry no browser identity
const GENERIC_UA_BRANDS = /Chromium|Google Chrome|Not.?A.?.?Brand/i;

const detectClientHintBrand = () => {
  const uaData = window.navigator.userAgentData;
  const brands = (uaData && uaData.brands) || [];
  const named = brands.find(b => b.brand && !GENERIC_UA_BRANDS.test(b.brand));

  return named ? named.brand : undefined;
};

const detectBrowser = ua => {
  if (/SamsungBrowser\//i.test(ua)) return 'Samsung Internet';
  if (/YaBrowser\//i.test(ua)) return 'Yandex Browser';
  if (/UCBrowser\//i.test(ua)) return 'UC Browser';
  if (/Vivaldi\//i.test(ua)) return 'Vivaldi';
  if (/Whale\//i.test(ua)) return 'Whale';
  if (/DuckDuckGo\/|Ddg\//i.test(ua)) return 'DuckDuckGo';
  if (/Edg\/|EdgiOS\/|EdgA\//i.test(ua)) return 'Edge';
  if (/OPR\/|OPT\//i.test(ua)) return 'Opera';

  // Brave and some forks mimic Chrome's user agent; Brave exposes its own API
  if (window.navigator.brave && typeof window.navigator.brave.isBrave === 'function') {
    return 'Brave';
  }

  // Chromium forks that hide in the UA may still declare themselves via client hints
  const brand = detectClientHintBrand();

  if (brand) return brand;

  if (/Chrome\/|CriOS\//i.test(ua)) return 'Chrome';
  if (/Firefox\/|FxiOS\//i.test(ua)) return 'Firefox';
  if (/Safari\//i.test(ua)) return 'Safari';

  return undefined;
};

/**
 * Best-effort device info for the auth request; unknown fields are omitted
 * (ipAddress/location are derived server-side and intentionally not sent)
 *
 * @returns {object}
 */
export const getDeviceInfo = () => {
  const ua = window.navigator.userAgent || '';
  const os = detectOs(ua);
  const browser = detectBrowser(ua);
  const isMobile = /Mobi|iPhone|iPad|iPod|Android/i.test(ua);

  const info = {
    deviceId: getDeviceId(),
    deviceType: isMobile ? 'Mobile Browser' : 'Desktop Browser'
  };

  if (os) info.os = os;
  if (browser) info.deviceName = os ? `${browser} (${os})` : browser;

  return info;
};
